/* ── Pickup Glow Helper ──────────────────────────────────────────
 * Adds a pulsing rim-light + faint emissive halo around an existing
 * pickup mesh so collectibles read clearly from a distance.
 *
 * Strategy: rather than rewriting `PickupRenderer`'s sprite material,
 * we add a soft additive "halo" sprite behind the existing base/glow
 * sprites and modulate its scale + opacity each frame with sin(t).
 * The pulse phase is seeded per-pickup so groups don't beat in unison.
 *
 * Public API matches the project's renderer convention:
 *   - constructor adds the halo sprite to the supplied parent
 *   - update(deltaMs) animates
 *   - dispose() cleans up
 *
 * Texture ownership: the constructor `texture` parameter is a SHARED
 * resource owned by the caller (typically `PickupRenderer.textures`,
 * which keys textures by URL and reuses them across every pickup of
 * the same type). `dispose()` MUST NOT call `texture.dispose()` — the
 * texture is referenced by the base + glow sprites of every active
 * pickup, plus other PickupGlow instances. Disposing it here would
 * corrupt every other pickup's render. The owner (PickupRenderer)
 * disposes the texture once when the whole renderer tears down.
 * ───────────────────────────────────────────────────────────── */

import * as THREE from 'three/webgpu';

const PULSE_SPEED = 2.4;
/** Min/max scale of the halo relative to the base sprite extents. */
const HALO_SCALE_MIN = 1.3;
const HALO_SCALE_MAX = 1.85;
/** Min/max opacity envelope for the rim-pulse. */
const HALO_OPACITY_MIN = 0.16;
const HALO_OPACITY_MAX = 0.42;

export interface PickupGlowAnchor {
  /** Width of the underlying sprite in world units; halo follows it. */
  baseWidth: number;
  /** Height of the underlying sprite in world units. */
  baseHeight: number;
  /** Y offset of the underlying sprite in its parent group's local space. */
  yOffset: number;
}

export class PickupGlow {
  readonly sprite: THREE.Sprite;
  readonly material: THREE.SpriteNodeMaterial;
  private readonly phase: number;
  private elapsed = 0;

  constructor(parent: THREE.Object3D, texture: THREE.Texture, color: THREE.Color, seed = 0) {
    // Per-pickup phase offset so adjacent pickups don't pulse in lockstep.
    this.phase = seed * 0.137 + Math.random() * Math.PI * 2;

    this.material = new THREE.SpriteNodeMaterial({
      map: texture,
      color: 0xffffff,
      transparent: true,
      opacity: HALO_OPACITY_MIN,
      alphaTest: 0.01,
      depthWrite: false,
      depthTest: true,
      sizeAttenuation: true,
      toneMapped: false,
      blending: THREE.AdditiveBlending,
    });
    this.material.color.copy(color);

    // Cast matches PickupRenderer.createPickupMesh (SpriteNodeMaterial isn't
    // typed as a SpriteMaterial subclass even though it is one at runtime).
    this.sprite = new THREE.Sprite(this.material as unknown as THREE.SpriteMaterial);
    this.sprite.frustumCulled = false;
    this.sprite.raycast = () => {};
    // Behind the existing glow (4) and base (5) sprites; outer ring only.
    this.sprite.renderOrder = 3;
    parent.add(this.sprite);
  }

  /**
   * Animate the rim-pulse for one frame. Anchor data flows in each call so
   * callers can resize/reposition the underlying pickup sprite at will (e.g.
   * the existing pulse + bob in PickupRenderer.updateSpriteMesh).
   */
  update(deltaMs: number, anchor: PickupGlowAnchor, color?: THREE.Color): void {
    this.elapsed += deltaMs / 1000;

    const wave = (Math.sin(this.elapsed * PULSE_SPEED + this.phase) + 1) * 0.5;
    const scale = THREE.MathUtils.lerp(HALO_SCALE_MIN, HALO_SCALE_MAX, wave);
    const opacity = THREE.MathUtils.lerp(HALO_OPACITY_MIN, HALO_OPACITY_MAX, wave);

    this.sprite.position.set(0, anchor.yOffset, -0.005);
    this.sprite.scale.set(anchor.baseWidth * scale, anchor.baseHeight * scale, 1);
    this.material.opacity = opacity;
    if (color) {
      this.material.color.copy(color);
    }
  }

  dispose(): void {
    if (this.sprite.parent) {
      this.sprite.parent.remove(this.sprite);
    }
    // NOTE: do NOT dispose `this.material.map` — the texture is shared
    // across every PickupGlow + sprite that uses the same pickup-type
    // image; PickupRenderer's URL-keyed cache owns its lifetime.
    this.material.dispose();
  }
}
